/**
 * 格式化工具函數
 * 用於格式化金額、日期等常用資料
 */

/**
 * 格式化金額為貨幣格式
 * @param {number|string} amount - 金額
 * @returns {string} 格式化後的金額，例如 "$100.00"
 */
export const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '$0.00';
    return `$${Math.abs(numAmount).toFixed(2)}`;
};

/**
 * 格式化日期時間（包含日期和時間）
 * @param {string|Date} dateTime - 日期時間字串或 Date 對象
 * @returns {string} 格式化後的日期時間，例如 "23 Jul 2025 00:00"
 */
export const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return dateTime;
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).replace(',', '');
};

/**
 * 格式化日期（只包含日期，不包含時間）
 * @param {string|Date} dateString - 日期字串或 Date 對象
 * @returns {string} 格式化後的日期，例如 "23/07/25"
 */
export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
};

/**
 * 格式化時間（只包含時間，不包含日期）
 * @param {string|Date} dateString - 日期字串或 Date 對象
 * @returns {string} 格式化後的時間，例如 "15:22"
 */
export const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

/**
 * 格式化日期為詳細格式（包含日期和時間，用於顯示）
 * @param {string|Date} dateString - 日期字串或 Date 對象
 * @returns {string} 格式化後的日期時間，例如 "23 Jul 2025 00:00"
 */
export const formatDateDetailed = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year} ${hours}:${minutes}`;
};

/**
 * 格式化日期為輸入框格式（YYYY-MM-DD）
 * @param {Date} date - Date 對象
 * @returns {string} 格式化後的日期，例如 "2025-12-01"
 */
export const formatDateForInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * 格式化日期為 API 格式（YYYY-MM-DD）
 * @param {string} dateString - 日期字串（通常是 YYYY-MM-DD 格式）
 * @returns {string} 格式化後的日期，例如 "2025-12-01"
 */
export const formatDateForAPI = (dateString) => {
    if (!dateString) return '';
    return dateString; // input type="date" 已經是 YYYY-MM-DD 格式
};

/**
 * 格式化數字（不帶貨幣符號）
 * @param {number|string} num - 數字
 * @returns {string} 格式化後的數字，例如 "100.00"
 */
export const formatNumber = (num) => {
    if (!num && num !== 0) return '0.00';
    return parseFloat(num).toFixed(2);
};

